import { useRef, useEffect } from 'react';

export const useDragScroll = () => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		let isDown = false;
		let startX = 0;
		let startY = 0;
		let scrollLeft = 0;
		let totalDistance = 0;
		const DRAG_THRESHOLD = 10; // Minimum distance in pixels to consider it a drag

		const handleMouseDown = (e: MouseEvent) => {
			isDown = true;
			totalDistance = 0;
			element.classList.add('cursor-grabbing');
			startX = e.pageX - element.offsetLeft;
			startY = e.pageY - element.offsetTop;
			scrollLeft = element.scrollLeft;
			e.preventDefault();
		};

		const handleMouseLeave = () => {
			isDown = false;
			element.classList.remove('cursor-grabbing');
		};

		const handleMouseUp = () => {
			isDown = false;
			element.classList.remove('cursor-grabbing');
		};

		const handleMouseMove = (e: MouseEvent) => {
			if (!isDown) return;
			e.preventDefault();

			const x = e.pageX - element.offsetLeft;
			const y = e.pageY - element.offsetTop;
			const walk = (x - startX) * 2; // Adjust scroll speed

			// Calculate total distance moved (both X and Y for better detection)
			const deltaX = Math.abs(x - startX);
			const deltaY = Math.abs(y - startY);
			const currentDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

			if (currentDistance > totalDistance) {
				totalDistance = currentDistance;
			}

			element.scrollLeft = scrollLeft - walk;
		};

		const handleClick = (e: MouseEvent) => {
			// Only prevent click if user has dragged beyond the threshold
			if (totalDistance > DRAG_THRESHOLD) {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		element.addEventListener('mousedown', handleMouseDown);
		element.addEventListener('mouseleave', handleMouseLeave);
		element.addEventListener('mouseup', handleMouseUp);
		element.addEventListener('mousemove', handleMouseMove);
		element.addEventListener('click', handleClick, true); // Use capture phase

		return () => {
			element.removeEventListener('mousedown', handleMouseDown);
			element.removeEventListener('mouseleave', handleMouseLeave);
			element.removeEventListener('mouseup', handleMouseUp);
			element.removeEventListener('mousemove', handleMouseMove);
			element.removeEventListener('click', handleClick, true);
		};
	}, []);

	return ref;
};
